import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import useFieldPending from '../../../hooks/useFieldPending';
import { InputGroupText, Button, InputGroup, Label, Tooltip } from 'reactstrap';
import { formatCurrency, formatNumber } from '../../../utils/numberUtils';
import { useTheme } from '../../../hooks/useTheme';
import { TextInput, CurrencyInput, NumberInput } from '../../../components/DataInputs';
import { parseUserInputtedNumber } from '../functions/quoteFunctions';
import { QuoteDataContext } from '../contexts/quoteDataContext';
import CustomSelect from '../../../components/CustomSelect';
import { useLockState } from '../../../components/userLocks/LockCheck';

const Pill = ({ text, top = 0, left = '7px', validationColor, disabled }) => (
    <span style={{
        position: 'absolute',
        top: top,
        transform: 'translateY(-50%)',
        left,
        display: 'inline-flex',
        alignItems: 'center',
        pointerEvents: 'none',
        fontSize: '0.7em',
        lineHeight: '1',
        color: 'var(--bs-secondary)',
        backgroundColor: 'var(--bg-color)',
        padding: '2px 4px 0 4px',
        borderRadius: '0.2rem',
    }}>
        {validationColor && (
            <span style={{ position: 'absolute', inset: 0, borderTop: `2px solid ${validationColor}`, borderTopLeftRadius: '0.2rem', borderTopRightRadius: '0.2rem', opacity: disabled ? 0.65 : 1, pointerEvents: 'none' }}>
                <svg style={{ position: 'absolute', left: 0, top: 0, width: 2, height: 'calc(0.5em + 1px)', display: 'block', pointerEvents: 'none' }}>
                    <line x1="1" y1="0" x2="1" y2="100%" style={{ stroke: validationColor, strokeWidth: 2 }}/>
                </svg>
                <svg style={{ position: 'absolute', right: 0, top: 0, width: 2, height: 'calc(0.5em + 1px)', display: 'block', pointerEvents: 'none' }}>
                    <line x1="1" y1="0" x2="1" y2="100%" style={{ stroke: validationColor, strokeWidth: 2 }}/>
                </svg>
            </span>
        )}
        <span className="px-1" style={{ opacity: disabled ? 0.65 : 1 }}>{text}</span>
    </span>
);

const BlankField = ({ id: propId, name, title }) => {
    const [iconHover, setIconHover] = useState(false);
    const id = propId ?? name;

    return (
        <div
            id={id}
            style={{ width: "100%" }}
            onMouseEnter={() => setIconHover(true)}
            onMouseLeave={() => setIconHover(false)}
        >
            <TextInput
                name={name}
                disabled={true}
                value={""}
            />
            {title && iconHover && (
                <Tooltip isOpen={true} target={id} placement="top">
                    {title}
                </Tooltip>
            )}
        </div>
    )
}

const PercentageField = ({ row, columnKey, hidden, onChange, disabled }) => {
    const rawValue = row[columnKey];

    let totalCost = 0;
    let totalPrice = 0;
    let totalOriginalPrice = 0;
    let totalOriginalCost = 0;
    let calculatedPercentage = 0;

    const [inputValue, setInputValue] = useState(!isNaN(rawValue) ? formatCurrency(rawValue) : '');
    const [hasFocus, setHasFocus] = useState(false);
    const [iconHover, setIconHover] = useState(false);

    const id = `${columnKey}-${row.id}`;

    let childHasValueDecreasedFromOriginal = false;
    let childHasValueIncreasedFromOriginal = false;
    let childValueDecreasedFromOriginalCount = 0;
    let childValueIncreasedFromOriginalCount = 0;

    let valueHasDecreasedFromOriginal = false;
    let valueHasIncreasedFromOriginal = false;

    let hasDiscountOutOfRange = false;
    let childHasDiscountOutOfRange = false;

    let showUpArrow = false;
    let showDownArrow = false;

    let tooltipClass = "";
    let borderColor = "";
    let tooltipText = "";

    const originalMargin = (item) => {

        if ((columnKey.toLowerCase().includes("recurring") || columnKey.toLowerCase().includes("annual")) && item.isRecurring && item.originalRecurringPrice !== 0 && item.originalRecurringPrice !== null && item.originalRecurringCost !== null) {
            // margin % doesn't change with quantity, or period length so no need to take those into account
            return ((item.originalRecurringPrice - item.originalRecurringCost) / item.originalRecurringPrice) * 100;
        } else if (columnKey.toLowerCase().includes("upfront") && item.isUpfront && item.originalUpfrontPrice !== 0 && item.originalUpfrontPrice !== null && item.originalUpfrontCost !== null) {
            // margin % doesn't change with quantity, so no need to take that into account
            return ((item.originalUpfrontPrice - item.originalUpfrontCost) / item.originalUpfrontPrice) * 100;
        } else if (columnKey.toLowerCase().includes("contract")) {
            // Because contract margin % takes into the relative weight of upfront vs recurring, then we
            // need to take into account quantity and num payments as both of these figures will affect the end result                                             
            const totalOriginalRecurringPrice = !item.isRecurring ? 0 : item.originalRecurringPrice * item.quantity * (item.recurringNumPayments > 0 ? item.recurringNumPayments : 1)
            const totalOriginalCost = !item.isRecurring ? 0 : item.originalRecurringCost * item.quantity * (item.recurringNumPayments > 0 ? item.recurringNumPayments : 1)
            const totalOriginalUpfrontPrice = !item.isUpfront ? 0 : item.originalUpfrontPrice * item.quantity
            const totalOriginalUpfrontCost = !item.isUpfront ? 0 : item.originalUpfrontCost * item.quantity

            if (totalOriginalRecurringPrice + totalOriginalUpfrontPrice !== 0) {
                return ((totalOriginalRecurringPrice + totalOriginalUpfrontPrice) - (totalOriginalCost + totalOriginalUpfrontCost)) / (totalOriginalRecurringPrice + totalOriginalUpfrontPrice) * 100;
            }
        }

        return 0;
    }

    const currentMargin = (item) => {
        if ((columnKey.toLowerCase().includes("recurring") || columnKey.toLowerCase().includes("annual")) && item.isRecurring && item.recurringPrice !== 0) {
            // margin % doesn't change with quantity, or period length so no need to take those into account
            return ((item.recurringPrice - item.recurringCost) / item.recurringPrice) * 100;
        } else if (columnKey.toLowerCase().includes("upfront") && item.isUpfront && item.upfrontPrice !== 0) {
            // margin % doesn't change with quantity, so no need to take that into account
            return ((item.upfrontPrice - item.upfrontCost) / item.upfrontPrice) * 100;
        } else if (columnKey.toLowerCase().includes("contract")) {
            // Because contract margin % takes into the relative weight of upfront vs recurring, then we
            // need to take into account quantity and num payments as both of these figures will affect the end result
            const totalRecurringPrice = !item.isRecurring ? 0 : item.recurringPrice * item.quantity * (item.recurringNumPayments > 0 ? item.recurringNumPayments : 1)
            const totalCost = !item.isRecurring ? 0 : item.recurringCost * item.quantity * (item.recurringNumPayments > 0 ? item.recurringNumPayments : 1)
            const totalUpfrontPrice = !item.isUpfront ? 0 : item.upfrontPrice * item.quantity
            const totalUpfrontCost = !item.isUpfront ? 0 : item.upfrontCost * item.quantity

            if (totalRecurringPrice + totalUpfrontPrice !== 0) {
                return ((totalRecurringPrice + totalUpfrontPrice) - (totalCost + totalUpfrontCost)) / (totalRecurringPrice + totalUpfrontPrice) * 100;
            }
        }

        return 0;
    }

    if (Array.isArray(row.items)) {
        // This is a group row with one or more child items

        for (const item of row.items.filter(x => !x.disabled)) {
            const value = item[columnKey];
            const multiplier = item.isCreditItem ? -item.quantity : item.quantity;

            if ((columnKey.toLowerCase().includes("recurring") ||
                columnKey.toLowerCase().includes("annual") ||
                columnKey.toLowerCase().includes("contract")) && item.isRecurring) {
                // Note that for annual columns, we still use the total recurring cost/price, as we are only calculating 
                // percentage here, so doesn't matter if the numbers being used are whole term or annualised.

                const recurringMultiplier = item.recurringNumPayments > 0 ? item.recurringNumPayments : 1

                totalCost += (item.recurringCost * multiplier * recurringMultiplier) || 0;
                totalPrice += (item.recurringPrice * multiplier * recurringMultiplier) || 0;
                totalOriginalCost += (item.originalRecurringCost * multiplier * recurringMultiplier) || 0;
                totalOriginalPrice += (item.originalRecurringPrice * multiplier * recurringMultiplier) || 0;
            }

            if ((columnKey.toLowerCase().includes("upfront") || columnKey.toLowerCase().includes("contract")) && item.isUpfront) {
                totalCost += (item.upfrontCost * multiplier) || 0;
                totalPrice += (item.upfrontPrice * multiplier) || 0;
                totalOriginalCost += (item.originalUpfrontCost * multiplier) || 0;
                totalOriginalPrice += (item.originalUpfrontPrice * multiplier) || 0;
            }

            if (columnKey.toLowerCase().includes("margin")) {
                if (Number(originalMargin(item).toFixed(2)) < Number(currentMargin(item).toFixed(2))) {
                    showUpArrow = true
                    childValueIncreasedFromOriginalCount += 1;
                    childHasValueIncreasedFromOriginal = true;
                }

                if (Number(originalMargin(item).toFixed(2)) > Number(currentMargin(item).toFixed(2))) {
                    showDownArrow = true
                    childValueDecreasedFromOriginalCount += 1;
                    childHasValueDecreasedFromOriginal = true;
                }
            }
        }


        if (columnKey.toLowerCase().includes("margin")) {
            calculatedPercentage = totalPrice != 0 ? ((totalPrice - totalCost) / totalPrice) * 100 : 0;

            if (totalPrice - totalCost < 0) {
                // This means we have a negative margin (it costs us more than we sold it for), so we need to invert the percentage
                calculatedPercentage = -calculatedPercentage;
            }

            if (childHasValueIncreasedFromOriginal) {
                tooltipClass = "text-success";
                //borderColor = "var(--bs-success)";
                tooltipText = `${childValueIncreasedFromOriginalCount} Item(s) have margins increased in value from the original`;
                showUpArrow = true;
            }

            if (childHasValueDecreasedFromOriginal) {
                tooltipClass = "text-warning";
                //borderColor = "var(--bs-warning)";
                tooltipText += tooltipText !== "" ? "<br /><br />" : "";
                tooltipText += `${childValueDecreasedFromOriginalCount} Item(s) have margins decreased in value from the original`;
                showDownArrow = true;
            }
        } else if (columnKey.toLowerCase().includes("discount") && columnKey.toLowerCase().includes("price")) {
            calculatedPercentage = totalOriginalPrice != 0 ? ((totalOriginalPrice - totalPrice) / totalOriginalPrice) * 100 : 0;
        } else if (columnKey.toLowerCase().includes("discount") && columnKey.toLowerCase().includes("cost")) {
            calculatedPercentage = totalOriginalPrice != 0 ? ((totalOriginalPrice - totalPrice) / totalOriginalPrice) * 100 : 0;
        }
    } else {
        // This is a single item row, not a group row

        const { upfrontDiscountIsWithinLimits } = getItemDiscountData(row, "upfrontPrice");
        const { recurringDiscountIsWithinLimits } = getItemDiscountData(row, "recurringPrice");
        const discountIsWithinLimits = upfrontDiscountIsWithinLimits && recurringDiscountIsWithinLimits;

        // This could be a margin, or a discount percentage
        calculatedPercentage = row[columnKey];

        if (columnKey.toLowerCase().includes("margin") && !row.isCreditItem) {

            if (Number(originalMargin(row).toFixed(2)) < Number(calculatedPercentage.toFixed(2))) {
                tooltipClass = "text-success";
                //borderColor = "var(--bs-success)";
                tooltipText = `Margin has been increased from ${formatNumber(originalMargin(row), 4)}% to ${formatNumber(calculatedPercentage, 4)}%`;
                showUpArrow = true;
                valueHasIncreasedFromOriginal = true;
            } else if (Number(originalMargin(row).toFixed(2)) > Number(calculatedPercentage.toFixed(2))) {
                if (discountIsWithinLimits) {
                    tooltipClass = "text-warning";
                    //borderColor = "var(--bs-warning)";
                } else {
                    tooltipClass = "text-danger";
                    borderColor = "var(--bs-danger)";
                    hasDiscountOutOfRange = true;
                }
                tooltipText = `Margin has been decreased from ${formatNumber(originalMargin(row), 4)}% to ${formatNumber(calculatedPercentage, 4)}%`;
                showDownArrow = true;
                valueHasDecreasedFromOriginal = true;
            }
        }
    }

    const { isPending, debouncedOnChange, markPending, flushAndCheckPending } = useFieldPending({
        rawValue: calculatedPercentage,
        onChange,
        onSafetyNetFired: (current) => setInputValue(!isNaN(current) ? formatCurrency(current) : ''),
    });

    // Sync the displayed value when the parent confirms the updated value.
    useEffect(() => {
        if (!isNaN(calculatedPercentage) && calculatedPercentage !== null && calculatedPercentage !== undefined) {
            if (!hasFocus) {
                setInputValue(formatCurrency(calculatedPercentage));
            }
        } else {
            setInputValue('');
        }
    }, [calculatedPercentage]);

    // Allow typing without formatting
    const handleChange = (e) => {
        var processedInput = parseUserInputtedNumber(e.target.value, 4)

        setInputValue(processedInput);
        markPending();

        // We let the user type 100% or more, but we'll never send anything above 99.9999 for calculations.
        // This means when the input loses focus it will correct itself to be within limits, but they are
        // free to have invalid numbers whilst typing.
        if (processedInput > 99.99) {
            processedInput = 99.99;
        }

        debouncedOnChange({ ...e, target: { ...e.target, value: Number(processedInput) } });
    };

    // Format on blur
    const handleBlur = () => {
        const stillPending = flushAndCheckPending();
        setHasFocus(false)

        // If we've dispatched a change that the parent hasn't reflected back yet,
        // keep showing the typed value — the rawValue useEffect will correct it
        // once the parent re-renders with the new calculated value.
        if (!stillPending) {
            const num = Number(inputValue);
            if (!isNaN(num)) {
                setInputValue(formatCurrency(calculatedPercentage));
            } else {
                setInputValue('');
            }
        }
    };

    // Unformat on focus
    const handleFocus = () => {
        setHasFocus(true)
        setInputValue(!isNaN(calculatedPercentage) ? formatNumber(calculatedPercentage, 4) : '');
    };

    const shouldShowTooltip = childHasValueDecreasedFromOriginal ||
        childHasValueIncreasedFromOriginal ||
        valueHasIncreasedFromOriginal ||
        valueHasDecreasedFromOriginal;

    const shouldShowErrorBorder = childHasDiscountOutOfRange || hasDiscountOutOfRange;

    return (
        <InputGroup className={isPending ? 'iq-pending-input' : undefined}>
            <NumberInput
                id={id}
                name={columnKey}
                isEditable={!disabled}
                numDecimalPlaces={2}
                className={"text-end"}
                type="percentage"
                value={inputValue}
                unformattedValue={calculatedPercentage}
                showUpArrow={showUpArrow}
                showDownArrow={showDownArrow}
                style={shouldShowTooltip ? {
                    textDecoration: "underline dotted",
                    textUnderlineOffset: "4px",
                    cursor: "help",
                    border: shouldShowErrorBorder ? `2px solid ${borderColor}` : undefined
                } : undefined}
                borderColor={borderColor}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onMouseEnter={() => setIconHover(true)}
                onMouseLeave={() => setIconHover(false)}
            />
            {(childHasValueDecreasedFromOriginal || childHasValueIncreasedFromOriginal || valueHasIncreasedFromOriginal || valueHasDecreasedFromOriginal) && (
                <Tooltip target={id} placement="top">
                    <div className={tooltipClass}
                        dangerouslySetInnerHTML={{ __html: tooltipText }}
                    >
                    </div>
                </Tooltip>
            )}
        </InputGroup>
    )
};

const getItemDiscountData = (row, columnKey) => {
    // This function assumes that columnKey is the unit field key,
    //  e.g. "upfrontPrice", "upfrontCost", "recurringPrice", "recurringCost"

    const rawValue = row[columnKey];
    const capitalizedKey = columnKey.charAt(0).toUpperCase() + columnKey.slice(1);
    const originalValue = row["original" + capitalizedKey];
    const latestValue = row["latest" + capitalizedKey];

    var discountPercent = 0;

    const isValidNumber = (value) => typeof value === 'number' && !isNaN(value);

    if (isValidNumber(originalValue) &&
        typeof rawValue === "number" && !isNaN(rawValue) &&
        originalValue > 0 && rawValue < originalValue) {
        discountPercent = ((originalValue - rawValue) / originalValue * 100)
    }

    const maxDiscountAllowed = row[columnKey + "DiscountThreshold"] ?? 0;

    const hasBeenDiscounted = originalValue != null && rawValue != null &&
        rawValue !== undefined && originalValue !== undefined &&
        !isNaN(rawValue) && !isNaN(originalValue) &&
        originalValue > rawValue;


    let minValueAllowed = 0;
    if (isValidNumber(originalValue)) {
        minValueAllowed = originalValue * (1 - maxDiscountAllowed / 100);
    } else if (typeof rawValue === "number" && !isNaN(rawValue)) {
        // If there is no original value, then we will accept any value entered
        minValueAllowed = rawValue;
    }

    const discountIsWithinLimits = (Number(minValueAllowed.toFixed(2)) <= Number((rawValue ?? 0).toFixed(2))) || !hasBeenDiscounted || (maxDiscountAllowed > 0 && discountPercent <= maxDiscountAllowed);

    const valueIncreasedFromOriginal = isValidNumber(originalValue) && isValidNumber(rawValue) && originalValue < rawValue;

    return {
        discountPercent,
        discountIsWithinLimits,
        hasBeenDiscounted,
        maxDiscountAllowed,
        minValueAllowed,
        latestValue,
        originalValue,
        valueIncreasedFromOriginal
    };
}

const TotalCurrencyField = ({ row, columnKey, disabled, hidden, onChange }) => {
    let totalValue = 0
    let incompleteCalculation = false;
    let childHasBeenDiscounted = false;
    let valueHasBeenDiscounted = false;
    let childHasValueIncreasedFromOriginal = false;
    let valueHasIncreasedFromOriginal = false;
    let hasDiscountOutOfRange = false;
    let childHasDiscountOutOfRange = false;
    let invalidChildCount = 0;
    let discountedChildCount = 0;
    let childValueIncreasedFromOriginalCount = 0;
    let showUpArrow = false;
    let showDownArrow = false;
    let dependantFieldIsMissing = false;

    const id = `${columnKey}-${row.id}`

    const originalValueField = columnKey.replace("total", "original");
    const unitFieldKey = columnKey.replace("total", "").charAt(0).toLowerCase() + columnKey.replace("total", "").slice(1);

    let tooltipClass = "";
    let borderColor = "";
    let tooltipText = "";

    if (!Array.isArray(row.items)) {
        incompleteCalculation = isNaN(row[columnKey])

        const { discountIsWithinLimits, hasBeenDiscounted, valueIncreasedFromOriginal } = getItemDiscountData(row, unitFieldKey);

        if (hasBeenDiscounted) {
            showDownArrow = true
            valueHasBeenDiscounted = true;
        } else if (valueIncreasedFromOriginal) {
            showUpArrow = true
            valueHasIncreasedFromOriginal = true;
        }

        hasDiscountOutOfRange = !discountIsWithinLimits;

        totalValue = row[columnKey]
    } else {
        for (const item of row.items.filter(x => !x.disabled)) {
            const value = item[columnKey];

            if (!isNaN(value)) {
                totalValue += value;

                const {
                    discountPercent,
                    discountIsWithinLimits,
                    hasBeenDiscounted,
                    maxDiscountAllowed,
                    minValueAllowed,
                    latestValue,
                    valueIncreasedFromOriginal
                } = getItemDiscountData(item, unitFieldKey);

                if (hasBeenDiscounted) {
                    showDownArrow = true
                    discountedChildCount += 1;
                    childHasBeenDiscounted = true;
                }

                if (valueIncreasedFromOriginal) {
                    showUpArrow = true
                    childValueIncreasedFromOriginalCount += 1;
                    childHasValueIncreasedFromOriginal = true;
                }

                if (!discountIsWithinLimits) {
                    invalidChildCount += 1;
                    childHasDiscountOutOfRange = true;
                }
            }
        }
    }

    const rawValue = useMemo(() => totalValue, [totalValue]);

    if (Array.isArray(row.items)) {
        if (childHasDiscountOutOfRange) {
            tooltipClass = "text-danger";
            borderColor = "var(--bs-danger)";
            tooltipText = `${invalidChildCount} Item(s) have exceeded the maximum discount allowed`;
        } else {
            if (childHasValueIncreasedFromOriginal) {
                tooltipClass = "text-success";
                borderColor = "var(--bs-success)";
                tooltipText = `${childValueIncreasedFromOriginalCount} Item(s) have been increased in value from the original`;
            }

            if (childHasBeenDiscounted) {
                tooltipClass = "text-success";
                borderColor = "var(--bs-success)";
                tooltipText += tooltipText !== "" ? "<br /><br />" : "";
                tooltipText += `${discountedChildCount} Item(s) have been discounted`;
            }
        }
    } else {
        if ((isNaN(rawValue) &&
            (columnKey.toLowerCase().includes("recurring") ||
                columnKey.toLowerCase().includes("contract") ||
                columnKey.toLowerCase().includes("annual")) && row.isRecurring)
        ) {
            dependantFieldIsMissing = true;
            tooltipClass = "text-danger";
            borderColor = "var(--bs-danger)";
            tooltipText = `Could not calculate total recurring value as "Quantity" or "Num Payments" or "Interval Months" is not set correctly`;
        } else if (hasDiscountOutOfRange) {
            tooltipClass = "text-danger";
            borderColor = "var(--bs-danger)";
            tooltipText = `Unit value discount exceeded`;
        } else if (valueHasIncreasedFromOriginal) {
            tooltipClass = "text-success";
            borderColor = "var(--bs-success)";
            tooltipText = `Unit value has been increased from the original`;
        } else if (valueHasBeenDiscounted) {
            tooltipClass = "text-success";
            borderColor = "var(--bs-success)";
            tooltipText = `Unit value has been discounted`;
        }
    }

    const shouldShowErrorBorder = childHasDiscountOutOfRange || hasDiscountOutOfRange || dependantFieldIsMissing || incompleteCalculation;

    const shouldShowTooltip = childHasBeenDiscounted ||
        valueHasBeenDiscounted ||
        childHasValueIncreasedFromOriginal ||
        valueHasIncreasedFromOriginal || dependantFieldIsMissing

    const [hasFocus, setHasFocus] = useState(false);
    const [iconHover, setIconHover] = useState(false);

    const calcInputValueFromRawValue = useCallback(() => {
        if (rawValue === null || rawValue === undefined || isNaN(rawValue)) {
            return '';
        }

        return hasFocus ? rawValue.toString() : formatCurrency(rawValue);
    }, [rawValue, hasFocus]);

    const [inputValue, setInputValue] = useState(calcInputValueFromRawValue());

    const { isPending, debouncedOnChange, markPending, flushAndCheckPending } = useFieldPending({
        rawValue,
        onChange,
        onSafetyNetFired: (current) => setInputValue(!isNaN(current) ? formatCurrency(current) : ''),
    });

    // Sync the displayed value when the parent confirms the updated value.
    useEffect(() => {
        if (!isNaN(rawValue) && rawValue !== null && rawValue !== undefined) {
            if (!hasFocus) {
                setInputValue(formatCurrency(rawValue));
            }
        } else {
            setInputValue('');
        }
    }, [rawValue]);

    // Allow typing without formatting
    const handleChange = (e) => {
        var processedInput = parseUserInputtedNumber(e.target.value, columnKey.toLowerCase().includes("price") ? 2 : 12)
        setInputValue(processedInput);
        markPending();
        debouncedOnChange({ ...e, target: { ...e.target, value: Number(processedInput), id: row.guid, name: columnKey, } });
    };

    // Format on blur
    const handleBlur = () => {
        const stillPending = flushAndCheckPending();
        setHasFocus(false)

        // Skip the reset if a change is still in-flight — the rawValue useEffect
        // will reformat once the parent confirms the new value.
        if (!stillPending) {
            if (!isNaN(rawValue)) {
                setInputValue(formatCurrency(rawValue));
            } else {
                setInputValue('');
            }
        }
    };

    // Unformat on focus
    const handleFocus = () => {
        setHasFocus(true)

        if (!isNaN(rawValue)) {
            // For total fields, we want to remove any rounding errors
            setInputValue(Number(rawValue.toFixed(2), 2));
        } else {
            setInputValue('');
        }
    };

    const handleClickUndo = () => {
        const totalOriginalFieldKey = columnKey.replace("total", "totalOriginal");

        const getOriginalTotalForItem = (item) => {
            const quantity = Number(item?.quantity);
            if (!Number.isFinite(quantity) || quantity === 0) return null;

            if (columnKey.toLowerCase().includes("recurring")) {
                const recurringIntervalMonths = Number(item?.recurringIntervalMonths);
                const recurringNumPayments = Number(item?.recurringNumPayments);
                if (!Number.isFinite(recurringIntervalMonths) || recurringIntervalMonths <= 0) return null;
                if (!Number.isFinite(recurringNumPayments) || recurringNumPayments <= 0) return null;

                const precomputedChildTotal = Number(item?.[totalOriginalFieldKey]);
                if (Number.isFinite(precomputedChildTotal)) return precomputedChildTotal;

                const originalUnitValue = Number(item?.[originalValueField]);
                if (!Number.isFinite(originalUnitValue)) return null;
                return originalUnitValue * quantity * recurringNumPayments;
            }

            const precomputedChildTotal = Number(item?.[totalOriginalFieldKey]);
            if (Number.isFinite(precomputedChildTotal)) return precomputedChildTotal;

            const originalUnitValue = Number(item?.[originalValueField]);
            if (!Number.isFinite(originalUnitValue)) return null;
            return originalUnitValue * quantity;
        };

        if (Array.isArray(row.items)) {

            // See if we already know the total, and if so, use that instead of recalculating it.
            const precomputedOriginalTotal = Number(row?.[totalOriginalFieldKey]);
            if (Number.isFinite(precomputedOriginalTotal)) {
                onChange?.({ target: { id: row.guid, name: columnKey, value: precomputedOriginalTotal } });
                return;
            }

            // We don't have a precomputed total, so we need to calculate it from the child items.
            let originalTotalValue = 0;
            for (const item of row.items.filter(x => !x.disabled)) {
                const itemOriginalTotal = getOriginalTotalForItem(item);
                if (itemOriginalTotal !== null) {
                    originalTotalValue += itemOriginalTotal;
                }
            }

            // Total fields are handled by total-field update logic upstream, so emit a total value here.
            onChange?.({ target: { id: row.guid, name: columnKey, value: originalTotalValue } });
        } else {
            const originalUnitValue = row?.[originalValueField];
            if (originalUnitValue !== null && originalUnitValue !== undefined && !Number.isNaN(Number(originalUnitValue))) {
                // Leaf rows can restore the unit field directly.
                onChange?.({ target: { id: row.guid, name: unitFieldKey, value: originalUnitValue } });
            }
        }

    }

    if (!row.items &&
        ((columnKey.toLocaleLowerCase().includes("recurring") && !row.isRecurring)
        || (columnKey.toLocaleLowerCase().includes("upfront") && !row.isUpfront))
    ) {
        return <CurrencyInput id={id} name={columnKey} title={"Item is not " + (columnKey.toLocaleLowerCase().includes("upfront") ? "upfront" : "recurring")}/>
    }    

    return (
        <InputGroup className={isPending ? 'iq-pending-input' : undefined}>
            <CurrencyInput
                id={id}
                name={columnKey}
                isEditable={!disabled}
                className={"text-end" + (incompleteCalculation ? " pe-8" : "")}
                type="text"
                value={inputValue}
                style={shouldShowTooltip ? {
                    textDecoration: "underline dotted",
                    textUnderlineOffset: "4px",
                    cursor: "help",
                    border: shouldShowErrorBorder ? `2px solid ${borderColor}` : undefined
                } : undefined}
                borderColor={shouldShowErrorBorder ? borderColor : undefined}
                showUpArrow={showUpArrow}
                showDownArrow={showDownArrow}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                allowUndo={!disabled && [ "totalUpfrontCost", "totalUpfrontPrice", "totalRecurringCost", "totalRecurringPrice"].includes(columnKey)}
                onClickUndo={handleClickUndo}
                onMouseEnter={() => setIconHover(true)}
                onMouseLeave={() => setIconHover(false)}
            />
            {shouldShowTooltip && iconHover && (
                <Tooltip 
                    isOpen={true} 
                    target={id} 
                    placement="top"
                >
                    <div className={tooltipClass}
                        dangerouslySetInnerHTML={{ __html: tooltipText }}
                    >
                    </div>
                </Tooltip>
            )}
        </InputGroup>
    )
}

/* 
    Will return nothing if this is a header row
*/
const CurrencyField = ({ row, columnKey, disabled, hidden, onChange }) => {
    const rawValue = row[columnKey];

    const [inputValue, setInputValue] = useState(!isNaN(rawValue) ? formatCurrency(rawValue) : '');
    const [hasFocus, setHasFocus] = useState(false);
    const [iconHover, setIconHover] = useState(false);

    const id = `${columnKey}-${row.id}`;

    const {
        discountPercent,
        discountIsWithinLimits,
        hasBeenDiscounted,
        maxDiscountAllowed,
        minValueAllowed,
        originalValue,
        latestValue,
        valueIncreasedFromOriginal
    } = getItemDiscountData(row, columnKey);

    let tooltipClass = "";
    let borderColor = "";
    let tooltipText = "";

    const originalValueField = "original" + columnKey.charAt(0).toUpperCase() + columnKey.slice(1);

    if (hasBeenDiscounted) {
        const delta = Math.abs(discountPercent - maxDiscountAllowed).toFixed(2);
        if (discountIsWithinLimits) {
            //tooltipClass = "text-success";
            //borderColor = "var(--bs-success)";
            tooltipText = `Original Value: £${formatCurrency(originalValue)}<br /><br />(Discount is within the allowed limit of ${maxDiscountAllowed}%)`;
        } else {
            tooltipClass = "text-danger";
            borderColor = "var(--bs-danger)";
            tooltipText = `Original Value: £${formatCurrency(originalValue)}<br /><br /><br />(Min Value Allowed: £${formatCurrency(minValueAllowed)})`;
        }
    } else if (valueIncreasedFromOriginal) {
        tooltipClass = "text-success";
        borderColor = "var(--bs-success)";
        tooltipText = `Original Value: £${formatCurrency(originalValue)}<br /><br />(Value has been increased from the original)`;
    }

    const { isPending, debouncedOnChange, markPending, flushAndCheckPending } = useFieldPending({
        rawValue,
        onChange,
        onSafetyNetFired: (current) => setInputValue(!isNaN(current) ? formatCurrency(current) : ''),
    });

    // Sync the displayed value when the parent confirms the updated value.
    useEffect(() => {
        if (!isNaN(rawValue) && rawValue !== null && rawValue !== undefined) {
            if (!hasFocus) {
                setInputValue(formatCurrency(rawValue));
            }
        } else {
            setInputValue('');
        }
    }, [rawValue]);

    // Allow typing without formatting
    const handleChange = (e) => {
        var processedInput = parseUserInputtedNumber(e.target.value, columnKey.toLowerCase().includes("price") ? 2 : 12)
        setInputValue(processedInput);
        markPending();
        debouncedOnChange({ ...e, target: { ...e.target, value: Number(processedInput), id: row.guid, name: columnKey }});
    };

    // Format on blur
    const handleBlur = () => {
        const stillPending = flushAndCheckPending();
        setHasFocus(false)

        // Skip the reset if a change is still in-flight — the rawValue useEffect
        // will reformat once the parent confirms the new value.
        if (!stillPending) {
            const num = Number(inputValue);
            if (!isNaN(num)) {
                setInputValue(formatCurrency(rawValue));
            } else {
                setInputValue('');
            }
        }
    };

    // Unformat on focus
    const handleFocus = () => {
        setHasFocus(true)
        setInputValue(!isNaN(rawValue) ? rawValue : '');
    };

    if (!Array.isArray(row.items)) {
        if (
            (columnKey.toLocaleLowerCase().includes("recurring") && !row.isRecurring)
            || (columnKey.toLocaleLowerCase().includes("upfront") && !row.isUpfront)
        ) {
            return <CurrencyInput id={id} name={columnKey} title={"Item is not " + (columnKey.toLocaleLowerCase().includes("upfront") ? "upfront" : "recurring")} />
        }

        return (
            <InputGroup className={isPending ? 'iq-pending-input' : undefined}>
                <CurrencyInput
                    id={id}
                    name={columnKey}
                    isEditable={!disabled}
                    className={"text-end"}
                    type="text"
                    value={inputValue}
                    originalValue={originalValue}
                    latestValue={latestValue}
                    unformattedValue={rawValue}
                    style={(hasBeenDiscounted || valueIncreasedFromOriginal) ? {
                        textDecoration: "underline dotted",
                        textUnderlineOffset: "4px",
                        cursor: "help",
                        border: !discountIsWithinLimits ? `2px solid ${borderColor}` : undefined
                    } : undefined}
                    borderColor={!discountIsWithinLimits ? borderColor : undefined}
                    showUpArrow={valueIncreasedFromOriginal}
                    showDownArrow={hasBeenDiscounted}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    allowUndo={!disabled && [ "upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice"].includes(columnKey)}
                    onClickUndo={() => {
                        onChange?.({ target: { id: row.guid, name: columnKey, value: row[originalValueField] } });
                    }}
                    onMouseEnter={() => setIconHover(true)}
                    onMouseLeave={() => setIconHover(false)}
              />
            {(hasBeenDiscounted || valueIncreasedFromOriginal) && iconHover && (
                <Tooltip 
                    isOpen={true} 
                    target={id} 
                    placement="top"
                >
                    <div className={tooltipClass}
                        dangerouslySetInnerHTML={{ __html: tooltipText }}
                    >                        
                    </div>
                </Tooltip>
            )}
          </InputGroup>
      )
    } else {
        return null;
    }
}

const IntegerField = ({ row, columnKey, disabled, hidden, onChange, title, maxValueAllowed, disabledText }) => {
    const rawValue = row[columnKey];
    const [inputValue, setInputValue] = useState(!isNaN(rawValue) ? rawValue : '');
    const [hasFocus, setHasFocus] = useState(false);
    const [iconHover, setIconHover] = useState(false);
    const id = `${columnKey}-${row.id}`;

    const { isPending, debouncedOnChange, markPending, flushAndCheckPending } = useFieldPending({
        rawValue,
        onChange,
        onSafetyNetFired: (current) => setInputValue(!isNaN(current) && current !== null ? formatNumber(current, 0) : ''),
    });

    // Sync the displayed value when the parent confirms the updated value.
    useEffect(() => {
        if (!isNaN(rawValue) && rawValue !== null && rawValue !== undefined) {
            if (!hasFocus) {
                setInputValue(formatNumber(rawValue, 0));
            }
        } else {
            setInputValue('');
        }
    }, [rawValue]);

    // Allow typing without formatting
    const handleChange = (e) => {
        var processedInput = parseUserInputtedNumber(e.target.value, 0)

        setInputValue(processedInput);
        markPending();

        if (maxValueAllowed !== undefined && processedInput > maxValueAllowed) {
            processedInput = maxValueAllowed;
        }

        debouncedOnChange({ ...e, target: { ...e.target, value: Number(processedInput) } });
    };

    // Format on blur
    const handleBlur = () => {
        const stillPending = flushAndCheckPending();
        setHasFocus(false)

        // Skip the reset if a change is still in-flight — the rawValue useEffect
        // will reformat once the parent confirms the new value.
        if (!stillPending) {
            const num = Number(inputValue);
            if (!isNaN(num)) {
                if (maxValueAllowed !== undefined && num > maxValueAllowed) {
                    setInputValue(Number(maxValueAllowed))
                } else {
                    setInputValue(rawValue)
                }
            } else {
                setInputValue('');
            }
        }
    };

    // Unformat on focus
    const handleFocus = () => {
        setHasFocus(true)
        setInputValue(!isNaN(rawValue) ? rawValue : '');
    };

    const tooltipMessage = title ?? row.validationErrors?.find(x => x.field === columnKey)?.error;

    if (!Array.isArray(row.items)) {

        return (
            <div
                id={id}
                className={isPending ? 'iq-pending-input' : undefined}
                style={{ width: "100%" }}
                onMouseEnter={() => setIconHover(true)}
                onMouseLeave={() => setIconHover(false)}
            >
                <InputGroup style={{}}>
                    <TextInput
                        className={"text-end"}
                        style={{border:
                            row.validationErrors?.find(x => x.field === columnKey)
                                ? row.validationErrors?.find(x => x.field === columnKey && x.severity === "error")
                                    ? "2px solid var(--bs-danger)"
                                    : "2px solid var(--bs-warning)"
                                : undefined}}
                        isEditable={!disabled}
                        value={disabled && disabledText ? disabledText : inputValue}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                </InputGroup>
                {tooltipMessage && iconHover && (
                    <Tooltip isOpen={true} target={id} placement="top">
                        {tooltipMessage}
                    </Tooltip>
                )}
            </div>
        )
    }
}

const TextField = ({ row, columnKey, disabled, hidden, onChange, title, pillText }) => {
    const rawValue = row[columnKey];
    const [inputValue, setInputValue] = useState(rawValue ?? '');
    const [hasFocus, setHasFocus] = useState(false);
    const [iconHover, setIconHover] = useState(false);
    const id = `${columnKey}-${row.id}`;

    const { isPending, debouncedOnChange, markPending, flushAndCheckPending } = useFieldPending({
        rawValue,
        onChange,
        onSafetyNetFired: (current) => setInputValue(current ?? ''),
    });

    // Sync the displayed value when the parent confirms the updated value.
    useEffect(() => {
        if (rawValue !== null && rawValue !== undefined) {
            if (!hasFocus) {
                setInputValue(rawValue);
            }
        } else {
            setInputValue('');
        }
    }, [rawValue]);

    const handleChange = (e) => {
        setInputValue(e.target.value);
        markPending();
        debouncedOnChange(e);
    };

    // Format on blur
    const handleBlur = () => {
        const stillPending = flushAndCheckPending();
        setHasFocus(false)
        // Skip the reset if a change is still in-flight — the rawValue useEffect
        // will update the display once the parent confirms the new value.
        if (!stillPending) {
            setInputValue(rawValue ?? '');
        }
    };

    // Unformat on focus
    const handleFocus = () => {
        setHasFocus(true)
        setInputValue(rawValue ?? '');
    };

    const validationError = row.validationErrors?.find(x => x.field === columnKey);
    const tooltipMessage = title ?? validationError?.error ?? inputValue;

    const validationColor = validationError
        ? validationError.severity === "error" ? "var(--bs-danger)" : "var(--bs-warning)"
        : null;

    const borderStyle = { border: validationColor ? `2px solid ${validationColor}` : undefined }

    if (!Array.isArray(row.items)) {
        return (
            <div
                id={id}
                className={isPending ? 'iq-pending-input' : undefined}
                style={{ width: "100%", position: 'relative' }}
                onMouseEnter={() => setIconHover(true)}
                onMouseLeave={() => setIconHover(false)}
            >
                <InputGroup style={{}}>
                    <TextInput
                        style={borderStyle}
                        isEditable={!disabled}
                        value={inputValue}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                </InputGroup>
                {tooltipMessage && iconHover && (
                    <Tooltip isOpen={true} target={id} placement="top">
                        {tooltipMessage}
                    </Tooltip>
                )}
                {pillText && <Pill text={pillText} left="7px" validationColor={validationColor} disabled={disabled} />}
            </div>
        )
    }
}

const SelectField = ({ row, columnKey, disabled, hidden, onChange, options = [], title, pillText }) => {
    const theme = useTheme();

    const rawValue = row[columnKey];
    const [inputValue, setInputValue] = useState(rawValue ?? '');
    const [hasFocus, setHasFocus] = useState(false);
    const [iconHover, setIconHover] = useState(false);

    const id = `${columnKey}-${row.id}`;

    useEffect(() => {
        if (!hasFocus) {
            setInputValue(rawValue ?? '');
        }
    }, [rawValue]);

    const handleChange = (selected) => {
        const value = selected?.value ?? '';
        setInputValue(value);
        onChange?.({ target: { name: columnKey, value } });
    };

    const handleBlur = () => { setHasFocus(false); };
    const handleFocus = () => { setHasFocus(true); };

    const tooltipMessage = title ?? row.validationErrors?.find(x => x.field === columnKey)?.error ?? options.find(option => option.value === inputValue)?.label ?? inputValue;

    const validationError = row.validationErrors?.find(x => x.field === columnKey);
    const validationColor = validationError
        ? validationError.severity === "error" ? "var(--bs-danger)" : "var(--bs-warning)"
        : null;

    const heightStyles = {
        control: (styles, { isDisabled }) => {
            const hasError = row.validationErrors?.find(x => x.field === columnKey && x.severity === "error");
            const hasWarning = row.validationErrors?.find(x => x.field === columnKey && x.severity === "warning");
            const validationColor = hasError ? "var(--bs-danger)" : hasWarning ? "var(--bs-warning)" : null;
            return {
                ...styles,
                borderColor: "transparent",
                outline: validationColor ? `2px solid ${validationColor}` : "none",
                outlineOffset: "0px",
                boxShadow: "none",
                minHeight: "auto",
                height: "calc(1.5em + 0.75rem)",
                "&:hover": { borderColor: "transparent" },
                backgroundColor: isDisabled
                    ? (theme === "dark-mode" ? "#1b272d" : "#E4E4E4")
                    : styles.backgroundColor,
                opacity: isDisabled ? 0.65 : 1,
            };
        },
        valueContainer: (styles) => ({
            ...styles,
            padding: "0 8px",
            height: "100%",
        }),
        input: (styles) => ({
            ...styles,
            margin: 0,
            padding: 0,
        }),
        singleValue: (styles) => ({
            ...styles,
            margin: 0,
            textAlign: "left",
        }),
        dropdownIndicator: (styles) => ({
            ...styles,
            padding: "2px 4px",
            color: theme === "dark-mode" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.4)",
            "&:hover": { color: theme === "dark-mode" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.6)" },
        }),
        indicatorSeparator: (styles) => ({
            ...styles,
            backgroundColor: "transparent",
        }),
        menu: (base) => ({
            ...base,
            width: 'auto',
            minWidth: base.width,
        }),
    };

    if (!Array.isArray(row.items)) {
        return (
            <div
                id={id}
                style={{ width: "100%", position: 'relative' }}
                onMouseEnter={() => setIconHover(true)}
                onMouseLeave={() => setIconHover(false)}
            >
                <CustomSelect
                    inputId={id}
                    value={inputValue}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    isDisabled={disabled}
                    options={[{ value: "", label: "-- Select --" }, ...options]}
                    styles={heightStyles}
                    sortItems={false}
                />
                {tooltipMessage && iconHover && (
                    <Tooltip isOpen={true} target={id} placement="top">
                        {tooltipMessage}
                    </Tooltip>
                )}
                {pillText && <Pill text={pillText} top="-2px" left="5px" validationColor={validationColor} disabled={disabled} />}
            </div>
        )
    }
}

const NestedTableQuoteItemColumn = ({ row, columnKey, disabled, hidden, onChange }) => {
    const context = useContext(QuoteDataContext);

    // If this component is used within a quote, then we have access to the context, and can 
    // extract the suppliers, item lookup types and salesforce codes to populate the table. 
    // If not, then we must be in module builder, and that doesn't show these fields. So we can 
    // just return empty arrays to avoid errors.
    const suppliers = context?.suppliers ?? [];
    const itemLookupTypes = context?.itemLookupTypes ?? [];
    const salesforceCodes = context?.salesforceCodes ?? [];

    const lockState = useLockState();

    const isLockedOrDisabled = disabled || lockState?.isLocked;

    const supplierOptions = useMemo(() =>
        [...suppliers || []]
            .map(x => ({ value: x.id, label: x.supplierName }))
            .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
    , [suppliers]);

    const itemLookupTypeOptions = useMemo(() =>
        [...itemLookupTypes || []]
            .map(x => ({ value: x.id, label: x.itemLookupTypeName }))
            .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
    , [itemLookupTypes]);
  
    const upfrontSalesforceCodeOptions = useMemo(() =>
        (salesforceCodes || [])
            .filter(x => x.type === "Upfront" || (x.type === "Recurring" && x.canBePaidUpfront))
            .map(x => ({ value: x.name, label: x.type === "Upfront" ? x.name : x.name + " 🔁" }))
            .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
    , [salesforceCodes]);

    const recurringSalesforceCodeOptions = useMemo(() =>
        (salesforceCodes || [])
            .filter(x => x.type === "Recurring")
            .map(x => ({ value: x.name, label: x.name }))
            .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
    , [salesforceCodes]);

    const upfrontSalesforceCodeNames = useMemo(() =>
        new Set((salesforceCodes || []).filter(x => x.type === "Upfront" || (x.type === "Recurring" && x.canBePaidUpfront)).map(x => x.name))
    , [salesforceCodes]);

    const recurringSalesforceCodeNames = useMemo(() =>
        new Set((salesforceCodes || []).filter(x => x.type === "Recurring").map(x => x.name))
    , [salesforceCodes]);

    const spreadableUpfrontSalesforceCodeNames = useMemo(() =>
        new Set((salesforceCodes || []).filter(x => x.type === "Recurring" && x.canBePaidUpfront).map(x => x.name))
    , [salesforceCodes]);

    if (hidden) return null;

    if (["totalUpfrontCost", "totalUpfrontPrice", "totalRecurringCost", "totalRecurringPrice", "totalContractCost", "totalContractPrice", "totalAnnualCost", "totalAnnualPrice", "totalUpfrontPriceDiscountValue", "totalRecurringPriceDiscountValue", "totalUpfrontMarginValue", "totalRecurringMarginValue", "totalAnnualMarginValue", "totalContractMarginValue", "totalAnnualPriceDiscountValue", "totalContractPriceDiscountValue", "totalOriginalUpfrontPrice", "totalOriginalRecurringPrice", "totalOriginalContractPrice", "totalOriginalUpfrontCost", "totalOriginalRecurringCost", "totalOriginalContractCost"].includes(columnKey)) {
        // These items will appear on the group line as well as the individual item
        return <TotalCurrencyField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled}
            hidden={hidden}
            onChange={onChange}
        />
    } else if (["upfrontCost", "originalUpfrontPrice", "originalUpfrontCost", "upfrontPrice", "recurringCost", "recurringPrice", "annualCost", "annualPrice", "upfrontPriceDiscountValue", "recurringPriceDiscountValue", "originalRecurringPrice", "originalRecurringCost", "upfrontMarginValue", "recurringMarginValue", "annualMarginValue", "contractMarginValue"].includes(columnKey)) {
        // These items will appear on the individual item line, and not the group or footer
        return <CurrencyField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled}
            hidden={hidden}
            onChange={onChange}
        />
    } else if (["quantity"].includes(columnKey)) {
        return <IntegerField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled}
            hidden={hidden}
            onChange={onChange}
            maxValueAllowed={100000}
        />
    } else if (["recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments"].includes(columnKey)) {
        if (row.items) {
            return null; // These fields should not be shown on group lines
        }

        const allowedToEdit = row.isRecurring && !row.quoteModuleGuid && row.source !== "Basket Processing";

        return <IntegerField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled || !allowedToEdit}
            disabledText={!row.isRecurring ? " " : undefined}
            title={!row.isRecurring 
                        ? "Only recurring items can have recurring payment details" 
                        : row.quoteModuleGuid 
                            ? "Cannot edit recurring payment details for module items"            
                            : row.source === "Basket Processing"
                                ? "Cannot edit recurring payment details for items added through basket processing"
                                : undefined
                        }
            hidden={hidden}
            onChange={onChange}
            maxValueAllowed={999}
        />
    } else if (["upfrontRecognitionMonths"].includes(columnKey)) {
        if (row.items) {
            return null; // These fields should not be shown on group lines
        }

        const allowedToEdit = row.isUpfront && !row.quoteModuleGuid && row.source !== "Basket Processing";
        const isSpreadableSFCode = row.isUpfront && spreadableUpfrontSalesforceCodeNames.has(row.upfrontSalesforceCode);

        return <IntegerField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled || !allowedToEdit || !isSpreadableSFCode}
            disabledText={!row.isUpfront || !isSpreadableSFCode ? " " : undefined}
            title={!row.isUpfront 
                        ? "Only upfront items support this field" 
                        : !isSpreadableSFCode
                            ? "This field is not applicable to the selected Salesforce code.  Change the Salesforce code to enable if needed."
                            : row.quoteModuleGuid 
                                ? "Cannot edit upfront payment details for module items"            
                                : row.source === "Basket Processing"
                                    ? "Cannot edit upfront payment details for items added through basket processing"
                                    : undefined
                        }
            hidden={hidden}
            onChange={onChange}
        />
    } else if (["upfrontMargin", "recurringMargin", "annualMargin", "contractMargin", "totalUpfrontMargin", "totalRecurringMargin", "totalAnnualMargin", "totalContractMargin", "upfrontPriceDiscountPercent", "recurringPriceDiscountPercent", "totalUpfrontPriceDiscountPercent", "totalRecurringPriceDiscountPercent", "totalAnnualPriceDiscountPercent", "totalContractPriceDiscountPercent"].includes(columnKey)) {
        return <PercentageField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled}
            hidden={hidden}
            onChange={onChange}
        />
    } else if (["internalName"].includes(columnKey)) {
        return <TextField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled || (row.quoteModuleGuid || row.source === "Basket Processing")}
            title={row.quoteModuleGuid ? "Internal name can only be changed for non-module items.  Decouple the module if you need to change it" : undefined}
            hidden={hidden}
            onChange={onChange}
        />
    } else if (["nameOnQuote"].includes(columnKey)) {
        return (
            <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                {row.isUpfront && (
                    <div style={{ flex: 1 }}>
                        <TextField
                            row={row}
                            columnKey={"upfrontNameOnQuote"}
                            disabled={isLockedOrDisabled}
                            hidden={hidden}
                            onChange={(e) => onChange({...e, target: { ...e.target, name: "upfrontNameOnQuote" } })}
                            pillText={ "Upfront" }
                        />
                    </div>
                )}
                {row.isRecurring && (
                    <div style={{ flex: 1 }}>
                        <TextField
                            row={row}
                            columnKey={"recurringNameOnQuote"}
                            disabled={isLockedOrDisabled}
                            hidden={hidden}
                            onChange={(e) => onChange({...e, target: { ...e.target, name: "recurringNameOnQuote" }})}
                            pillText={"Recurring"}
                        />
                    </div>
                )}
            </div>
        )
    } else if (columnKey === "autotaskCode") {
        return <TextField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled || row.itemLookupId}
            title={row.itemLookupId ? "Autotask code can only be changed for non-catalogue items" : undefined}
            hidden={hidden}
            onChange={onChange}
        />   
    } else if (columnKey === "salesforceCode") {
        if (row.items) return null;
        
        return (
            <div style={{ display: "flex", gap: "4px", width: "100%" }}>
                {row.isUpfront &&
                    <div style={{ position: "relative", flex: 1 }}>
                        <SelectField
                            row={row}
                            columnKey={"upfrontSalesforceCode"}
                            disabled={isLockedOrDisabled || row.itemLookupId}
                            title={row.itemLookupId ? "Salesforce code can only be changed for non-catalogue items" : undefined}
                            hidden={hidden}
                            onChange={onChange}
                            options={[
                                ...(row.isUpfront && row.upfrontSalesforceCode && !upfrontSalesforceCodeNames.has(row.upfrontSalesforceCode) ? [{ value: row.upfrontSalesforceCode, label: row.upfrontSalesforceCode + " (INVALID)" }] : []),
                                ...upfrontSalesforceCodeOptions
                            ]}
                            pillText={ "Upfront"  }
                        />
                    </div>
                }
                {row.isRecurring &&
                    <div style={{ position: "relative", flex: 1 }}>
                        <SelectField
                            row={row}
                            columnKey={"recurringSalesforceCode"}
                            disabled={isLockedOrDisabled || row.itemLookupId}
                            title={row.itemLookupId ? "Salesforce code can only be changed for non-catalogue items" : undefined}
                            hidden={hidden}
                            onChange={onChange}
                            options={[
                                ...(row.isRecurring && row.recurringSalesforceCode && !recurringSalesforceCodeNames.has(row.recurringSalesforceCode) ? [{ value: row.recurringSalesforceCode, label: row.recurringSalesforceCode + " (INVALID)" }] : []),
                                ...recurringSalesforceCodeOptions
                            ]}
                            pillText={ "Recurring"}
                        />
                    </div>
                }
            </div>
        )
    } else if (columnKey === "supplierId") {
        return <SelectField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled || row.itemLookupId}
            title={row.itemLookupId ? "Supplier can only be changed for non-catalogue items" : undefined}
            hidden={hidden}
            onChange={onChange}
            options={supplierOptions}
        />
    } else if (columnKey === "itemLookupTypeId") {
        return <SelectField
            row={row}
            columnKey={columnKey}
            disabled={isLockedOrDisabled || row.itemLookupId}
            title={row.itemLookupId ? "Type can only be changed for non-catalogue items" : undefined}
            hidden={hidden}
            onChange={onChange}
            options={itemLookupTypeOptions}
        />
    } else if (columnKey === "source") {
        if (row.items) return null; // Parent rows don't have a source, only individual items do

        let icon = "bi-question-circle";

        switch (row.source) {
            case "Custom":
                icon = "bi-pencil-square";
                break;
            case "Catalogue":
                icon = "bi-collection";
                break;
            case "Module":
                icon = "bi-card-checklist";
                break;
            case "Basket Processing":
                icon = "bi-basket";
                break;
            case "Import":
                icon = "bi-file-earmark-arrow-up";
                break;
        }

        return <i className={`bi ${icon}`} style={{ fontSize: "14px" }} title={row.source}></i>;
    } else {
        return (
            <span>"{columnKey}" not recognised</span>
        )
    }
};

export default NestedTableQuoteItemColumn;
